import { ReactNode } from "react";
import { GetProfileQueryQuery } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";

import ProfileGoalsTab from "./profile-goals-tab";
import ProfileItems from "./profile-items";

export type ProfileContentTab = {
  value: string;
  label: string;
  available: boolean;
  render: () => ReactNode;
};

// The profile "content" tabs. Goals get their own top-level tab; recognitions,
// feedback and action items are combined into a single "Items" tab (with its
// own sub-tabs); reviews now live at the top of the Career tab. Centralizes
// which sections exist (org feature flags) and how to render each, so
// profile-page.tsx can build both the tab buttons and their routes from one
// source of truth.
const useProfileContentTabs = (
  profile: GetProfileQueryQuery["user"] | undefined,
): ProfileContentTab[] => {
  const label = useLabel();
  const organization = currentOrganizationVar();

  if (!profile) return [];

  const hasAnyItemType = !!(
    organization.featureFlags.actionItems ||
    organization.featureFlags.feedbacks ||
    organization.featureFlags.recognitions
  );

  return [
    {
      value: "goals",
      label: label("goal", { titleCase: true, pluralize: true }),
      available: !!organization.featureFlags.goals,
      render: () => <ProfileGoalsTab profile={profile} />,
    },
    {
      value: "items",
      label: "Items",
      available: hasAnyItemType,
      render: () => <ProfileItems profile={profile} />,
    },
  ];
};

export default useProfileContentTabs;
